// React Library
import React, { useCallback, useEffect, useState } from 'react';
// MUI Components and icons
import {
    Box,
    ImageList,
    ImageListItem,
    Typography,
    ImageListItemBar,
    IconButton,
    useMediaQuery,
    Button,
    Snackbar,
    Alert,
    CircularProgress,
    Tooltip
} from '@mui/material'
import CancelIcon from '@mui/icons-material/Cancel';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';

// uuid
import { v4 as uuidv4 } from 'uuid';
//lodash
import isEmpty from 'lodash/isEmpty';

// User defined functions and components
import HttpService from '../../services/HttpService';
import Constant from '../../util/constants';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@emotion/react';
import UploadPhoto from '../common/UploadPhoto';
import Token from '../../util/Token';

const styles = {
  uploadBtn: {
    height: '-webkit-fill-available'
  },
  itemListBar: {
    background: 'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, ' +
        'rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
    '& button': {
      color: '#fff'
    }
  }
}

const initialState = {
  imageList: [],
  loading: !1
}

function Photos({ location, refreshLocationDetail }) {
  const [photoState, setPhotoState] = useState(initialState)
  const [photoModal, setPhotoModal] = useState({open: false, src: ""});

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const parsedToken = Token.parsedToken();
  const currentUserId = parsedToken?.success ? (parsedToken.data?._id || parsedToken.data?.id || parsedToken.data?.userId) : null;

  const getPhotoList = useCallback((photo, list) => {
    const length = photo.length;
    for(let p = length - 1; p >= 0; p--) {
      const isObj = typeof photo[p] === 'object' && photo[p] !== null;
      const imgUrl = isObj ? photo[p].url : photo[p];
      const uploadedBy = isObj ? photo[p].uploadedBy : null;

      list.push({
        img: imgUrl,
        uploadedBy: uploadedBy,
        title: `location image ${p}`,
        id: uuidv4(),
        style: {}
      });
    }
    return list;
  }, []);

  const [previewSrc, setPreviewSrc] = useState(null);
  const [selectedFileBinary, setSelectedFileBinary] = useState(null);
  const [errorToast, setErrorToast] = useState({ open: false, message: "" });
  const [isUploading, setIsUploading] = useState(false);

  const MAX_FILE_SIZE_MB = 5;

  const handleTriggerUpload = useCallback(() => {
    if (!selectedFileBinary || !location._id) return;
    setIsUploading(true);
    
    // Fetch dynamically to avoid stale closures
    const tkn = Token.parsedToken();
    const activeUserId = tkn?.success ? (tkn.data?._id || tkn.data?.id || tkn.data?.userId) : null;

    HttpService(
      Constant.methods.UPLOAD_IMAGE,
      { id: location._id, image: selectedFileBinary, userId: activeUserId } 
    ).then((res) => {
      if(res.status) {
        const { data: { photos } } = res;
        const _list = getPhotoList(photos, []);
        setPhotoState({ imageList: _list });
        setPreviewSrc(null);
        setSelectedFileBinary(null);
        if (refreshLocationDetail) {
           refreshLocationDetail();
        }
      } else {
        setErrorToast({ open: true, message: "Image upload failed. Please try again." });
      }
    }).finally(() => {
      setIsUploading(false);
    });
  }, [selectedFileBinary, location._id, getPhotoList, refreshLocationDetail]);

  const handleImageUpload = useCallback((e) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;
    
    const file = fileList[0];
    const fileSizeInMB = file.size / (1024 * 1024);

    if (fileSizeInMB > MAX_FILE_SIZE_MB) {
      setErrorToast({ open: true, message: `File size exceeds the limit of ${MAX_FILE_SIZE_MB}MB.` });
      return;
    }

    const fileReader = new FileReader();

    fileReader.onload = function() {
      const objectURL = URL.createObjectURL(file);
      setPreviewSrc(objectURL);
    }

    fileReader.onloadend = (reader) => {
      setSelectedFileBinary(reader.target.result);
    }

    fileReader.readAsDataURL(file);
  }, []);

  const handleBinaryUpload = useCallback((binaryString) => {
    if (!binaryString) {
       // Called when user clicks "Deselect" / Cancel
       setPreviewSrc(null);
       setSelectedFileBinary(null);
       return;
    }
    
    // Convert base64 to File object to reuse logical flow
    fetch(binaryString)
      .then(res => res.blob())
      .then(blob => {
        const file = new File([blob], "uploaded_image.png", { type: "image/png" });
        const e = { target: { files: [file] } };
        handleImageUpload(e);
      });
  }, [handleImageUpload]);

  const handleDeleteImage = (imagePath) => async (e) => {
    e.stopPropagation();
      const tkn = Token.parsedToken();
      const activeUserId = tkn?.success ? (tkn.data?._id || tkn.data?.id || tkn.data?.userId) : null;

      const deleteRes = await HttpService(Constant.methods.REMOVE_IMAGE, { 
        id: location._id, 
        imagePath: imagePath,
        userId: activeUserId
      });
    if (deleteRes.status) {
      // Update local state immediately from response
      if (deleteRes.data && !Array.isArray(deleteRes.data) && deleteRes.data.photos) {
        const _list = getPhotoList(deleteRes.data.photos, []);
        setPhotoState(prevState => ({
          ...prevState,
          imageList: _list
        }));
      } else if (deleteRes.data && Array.isArray(deleteRes.data) && deleteRes.data[0] && deleteRes.data[0].photos) {
        const _list = getPhotoList(deleteRes.data[0].photos, []);
        setPhotoState(prevState => ({
          ...prevState,
          imageList: _list
        }));
      }

      // Tell parent to refresh its global cache
      if (refreshLocationDetail) {
         refreshLocationDetail();
      }
    }
  };

  useEffect(() => {
    if(!isEmpty(location)) {
      const list = [];
      const { photos } = location;
      if (photos) {
         const _list = getPhotoList(photos, list);
         setPhotoState(prevState => ({
           ...prevState,
           imageList: _list
         }));
      }
    }
  }, [location, getPhotoList]);

  return (
      <Box className="cm-photos">
          <ImageList cols={3} rowHeight={90} sx={{ overflow: "visible" }}>
              <ImageListItem cols={3} sx={{ height: 'auto !important', mb: 2 }}>
                  <UploadPhoto
                    setImageSrc={(val) => {
                       if (!val) {
                          setPreviewSrc(null);
                          setSelectedFileBinary(null);
                       }
                    }} 
                    imageSrc={previewSrc}
                    uploadImage={handleBinaryUpload}
                  />
                  {previewSrc && (
                    <Box sx={{ mt: 1 }}>
                       <Button 
                         variant="contained" 
                         color="primary" 
                         fullWidth
                         onClick={handleTriggerUpload}
                         disabled={isUploading}
                       >
                         {isUploading ? <CircularProgress size={24} color="inherit" /> : 'Save Photo'}
                       </Button>
                    </Box>
                  )}
                  {photoState.imageList.length === 0 && !previewSrc && (
                      <Box sx={{ mt: 3, textAlign: 'center', opacity: 0.6 }}>
                          <PhotoLibraryOutlinedIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                          <Typography variant="subtitle1" color="text.secondary">
                              No Photos Provided
                          </Typography>
                      </Box>
                  )}
              </ImageListItem>

              {photoState.imageList.map((item) => (
                  <ImageListItem key={item.id} sx={{ position: 'relative' }}>
                    <img
                        src={item.img}
                        srcSet={item.img}
                        alt={item.title}
                        loading="lazy"
                        style={item.style}
                        onClick={() => {setPhotoModal({open: true, src: item.img})}}
                    />
                    {currentUserId && item.uploadedBy === currentUserId && (
                      <Tooltip title="Delete Photo" placement="top">
                        <IconButton
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            padding: '4px',
                            '&:hover': {
                               backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            }
                          }}
                          onClick={handleDeleteImage(item.img)}
                        >
                           <CancelIcon color="error" fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </ImageListItem>
              ))}

              <Dialog
                   onClose={() => {setPhotoModal({open: false, src: ""})}}
                   aria-labelledby="customized-dialog-title"
                   open={photoModal.open}
              >
                  <Tooltip title="Close Photo" placement="bottom">
                  <IconButton
                    aria-label="close"
                    onClick={() => {setPhotoModal({open: false, src: ""})}}
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                    }}
                  >
                    <CloseIcon />
                    </IconButton>
                    </Tooltip>
                    <DialogContent sx={{minHeight: "300px", minWidth: isSmallScreen ? "100%" : "600px", display: "flex", placeItems: "center", placeContent: "center", padding: isSmallScreen ? "30px 16px" : "32px 40px"}}>
                      <img src= {photoModal.src} alt="Review" />
                    </DialogContent>
              </Dialog>
          </ImageList>

          <Snackbar 
             open={errorToast.open} 
             autoHideDuration={6000} 
             onClose={() => setErrorToast({ ...errorToast, open: false })}
             anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
             <Alert onClose={() => setErrorToast({ ...errorToast, open: false })} severity="error" sx={{ width: '100%' }}>
               {errorToast.message}
             </Alert>
          </Snackbar>    
      </Box>
  )
}

export default React.memo(Photos)